import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Link } from "react-router-dom";
import { useState } from "react";
import { Clock, MapPin, AlertTriangle, CheckCircle2, DoorOpen, ChevronRight, Bath, UtensilsCrossed, Sparkles } from "lucide-react";

const AREA_ICONS = {
  "Guest Room": DoorOpen,
  "Bathroom": Bath,
  "Kitchen": UtensilsCrossed,
  "Reception": Sparkles,
  "Public": Sparkles,
  "Hallway": Sparkles,
};

function statusPill(status) {
  const map = {
    assigned: { bg: "hsl(38 92% 90%)", fg: "hsl(38 92% 35%)", label: "Pending" },
    in_progress: { bg: "hsl(158 74% 92%)", fg: "hsl(158 74% 30%)", label: "In Progress" },
    completed: { bg: "hsl(220 15% 90%)", fg: "hsl(220 30% 30%)", label: "Awaiting Review" },
    verified: { bg: "hsl(158 74% 92%)", fg: "hsl(158 74% 30%)", label: "Verified" },
    rejected: { bg: "hsl(0 78% 94%)", fg: "hsl(0 78% 45%)", label: "Rejected" },
    overdue: { bg: "hsl(0 78% 94%)", fg: "hsl(0 78% 45%)", label: "Overdue" },
    pending: { bg: "hsl(220 15% 90%)", fg: "hsl(220 30% 30%)", label: "Pending" },
  };
  return map[status] || map.pending;
}

function TaskCard({ t, areaMap, danger }) {
  const area = areaMap[t.area_id];
  const AreaIcon = AREA_ICONS[area?.area_type] || DoorOpen;
  const pill = statusPill(t.status);
  const time = new Date(t.due_at).toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" });

  return (
    <Link
      to={`/app/tasks/${t.id}`}
      className={`block rounded-2xl border ${danger ? "border-destructive/40 bg-destructive/[0.03]" : "border-border bg-card hover:border-primary/40"} p-4 hover:shadow-md transition-all group`}
      data-testid={`my-task-${t.id}`}
    >
      <div className="flex items-center gap-4">
        <div className="w-14 h-14 rounded-2xl bg-primary/10 text-primary flex flex-col items-center justify-center shrink-0">
          <span className="text-[0.6rem] font-semibold tracking-wider">{time.split(" ")[1] || ""}</span>
          <span className="text-sm font-bold font-mono leading-none">{time.split(" ")[0]}</span>
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <AreaIcon className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
            <div className="font-semibold text-sm truncate">{area?.name || t.title}</div>
          </div>
          <div className="text-xs text-muted-foreground mt-0.5 truncate">
            {t.title}
          </div>
          <div className="mt-2">
            <span
              className="inline-flex items-center text-[0.68rem] font-semibold px-2.5 py-0.5 rounded-full"
              style={{ background: pill.bg, color: pill.fg }}
            >
              {pill.label}
            </span>
          </div>
        </div>
        <ChevronRight className="w-4 h-4 text-muted-foreground group-hover:text-primary group-hover:translate-x-0.5 transition-all shrink-0" />
      </div>
    </Link>
  );
}

export default function MyTasks() {
  const { user } = useAuth();
  const [tab, setTab] = useState("active");
  const { data: tasks } = useQuery({
    queryKey: ["my-tasks"],
    queryFn: async () => (await api.get("/tasks")).data,
    refetchInterval: 10000,
  });
  const { data: areas } = useQuery({ queryKey: ["areas-all"], queryFn: async () => (await api.get("/facilities/areas/all")).data });
  const areaMap = Object.fromEntries((areas || []).map((a) => [a.id, a]));

  const now = new Date();
  const my = (tasks || []).filter((t) => t.assigned_to === user.id);
  const overdue = my.filter((t) => (t.status === "overdue") || (["assigned","in_progress"].includes(t.status) && new Date(t.due_at) < now));
  const overdueIds = new Set(overdue.map((t) => t.id));
  const active = my.filter((t) => ["assigned","in_progress","rejected"].includes(t.status) && !overdueIds.has(t.id));
  const done = my.filter((t) => ["completed","verified"].includes(t.status)).slice(0, 20);

  const dateStr = now.toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric" });

  return (
    <div className="p-4 md:p-8 max-w-3xl mx-auto md:mx-0">
      <div className="mb-6">
        <div className="text-[0.7rem] tracking-widest uppercase text-muted-foreground mb-2 font-semibold">Today, {dateStr}</div>
        <h1 className="font-display text-3xl md:text-4xl font-black tracking-tight">
          Hi, {user?.name?.split(" ")[0]} 👋
        </h1>
        <p className="text-sm text-muted-foreground mt-2">
          {active.length + overdue.length} task{active.length + overdue.length !== 1 ? "s" : ""} on deck.
        </p>
      </div>

      {/* Tab pills */}
      <div className="flex items-center gap-2 mb-6 p-1 bg-muted rounded-full w-fit">
        <button
          onClick={() => setTab("active")}
          className={`px-5 py-2 rounded-full text-sm font-semibold transition-colors ${tab === "active" ? "bg-primary text-primary-foreground shadow" : "text-muted-foreground hover:text-foreground"}`}
          data-testid="tab-active"
        >
          My Tasks
        </button>
        <button
          onClick={() => setTab("completed")}
          className={`px-5 py-2 rounded-full text-sm font-semibold transition-colors ${tab === "completed" ? "bg-primary text-primary-foreground shadow" : "text-muted-foreground hover:text-foreground"}`}
          data-testid="tab-completed"
        >
          Completed
        </button>
      </div>

      {tab === "active" ? (
        <>
          {overdue.length > 0 && (
            <section className="mb-6">
              <div className="flex items-center gap-2 mb-3">
                <AlertTriangle className="w-4 h-4 text-destructive" />
                <div className="font-display font-semibold text-destructive text-sm">Overdue · {overdue.length}</div>
              </div>
              <div className="space-y-2">{overdue.map((t) => <TaskCard key={t.id} t={t} areaMap={areaMap} danger />)}</div>
            </section>
          )}

          <section>
            <div className="text-[0.7rem] tracking-widest uppercase text-muted-foreground font-semibold mb-3">Next up</div>
            <div className="space-y-2">
              {active.map((t) => <TaskCard key={t.id} t={t} areaMap={areaMap} />)}
              {active.length === 0 && overdue.length === 0 && (
                <div className="border border-dashed border-border rounded-2xl p-10 text-center">
                  <CheckCircle2 className="w-8 h-8 mx-auto mb-3 text-primary" />
                  <div className="font-semibold">You're all caught up.</div>
                  <p className="text-sm text-muted-foreground mt-1">No tasks assigned right now.</p>
                </div>
              )}
            </div>
          </section>
        </>
      ) : (
        <section>
          <div className="text-[0.7rem] tracking-widest uppercase text-muted-foreground font-semibold mb-3">Recent completions</div>
          <div className="space-y-2">
            {done.map((t) => <TaskCard key={t.id} t={t} areaMap={areaMap} />)}
            {done.length === 0 && (
              <div className="border border-dashed border-border rounded-2xl p-10 text-center text-sm text-muted-foreground">
                No completed tasks yet.
              </div>
            )}
          </div>
        </section>
      )}
    </div>
  );
}
